
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/contexts/ProfileContext';
import { businessRulesEngine } from '@/core/BusinessRulesEngine';
import { debugLogger } from '@/utils/debugLogger';

interface ProfileViewsAccess {
  canViewProfileViews: boolean;
  shouldShowBlur: boolean;
  loading: boolean;
  adminBypass: boolean;
}

export const useProfileViewsAccess = () => {
  const { user } = useAuth();
  const { profile: userProfile } = useProfile();
  const [access, setAccess] = useState<ProfileViewsAccess>({
    canViewProfileViews: true,
    shouldShowBlur: false,
    loading: true,
    adminBypass: false
  });

  const checkProfileViewsAccess = useCallback(async () => {
    if (!user?.id) {
      setAccess({
        canViewProfileViews: false,
        shouldShowBlur: false,
        loading: false,
        adminBypass: false
      });
      return;
    }

    if (!userProfile) {
      setAccess({
        canViewProfileViews: true,
        shouldShowBlur: false,
        loading: false,
        adminBypass: false
      });
      return;
    }

    try {
      setAccess(prev => ({ ...prev, loading: true }));

      debugLogger.debug('ProfileViewsAccess', 'Checking profile views access for user:', {
        plan_type: userProfile.plan_type, 
        role: userProfile.role
      });

      // Use Business Rules Engine to check profile views access
      const accessResult = await businessRulesEngine.canViewConnections({
        id: user.id,
        plan_type: userProfile.plan_type || 'sponsor_free',
        role: userProfile.role,
        hidden: userProfile.hidden || false,
        verification_level: 'basic'
      });

      const shouldShowBlur = accessResult.data?.showBlur || false;
      const adminBypass = accessResult.data?.adminBypass || false;

      setAccess({
        canViewProfileViews: accessResult.allowed,
        shouldShowBlur,
        loading: false,
        adminBypass
      });

      debugLogger.debug('ProfileViewsAccess', 'Profile views access check result:', {
        canViewProfileViews: accessResult.allowed,
        shouldShowBlur,
        adminBypass,
        reason: accessResult.reason
      });

    } catch (error) {
      debugLogger.error('ProfileViewsAccess', 'Error checking profile views access:', error);
      setAccess({
        canViewProfileViews: true,
        shouldShowBlur: false,
        loading: false,
        adminBypass: false
      });
    }
  }, [user?.id, userProfile]);

  useEffect(() => {
    checkProfileViewsAccess();
  }, [checkProfileViewsAccess]);

  return {
    ...access,
    refreshAccess: checkProfileViewsAccess
  };
};
